import { Modal, Form, Select, DatePicker, InputNumber, Input, Row, Col, Alert, Button, Tag, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { usePermission } from '../../store/auth';
import { bankAcctLabel, bankAcctOption } from './PaymentFormModal';
import { inr2 } from '../../lib/format';
import { RetailerLink } from '../../components/EntityLinks';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';

const APPROVAL_TONE: Record<string, string> = { APPROVED: 'green', PENDING: 'gold', REJECTED: 'red' };
const APPROVAL_LABEL: Record<string, string> = { APPROVED: 'Approved', PENDING: 'Awaiting review', REJECTED: 'Rejected' };

const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];

export interface EditablePayment {
  id: string;
  paymentNo: string;
  retailerId: string;
  // Carried from the list row so the picker can always name this retailer — see `retailerOpts`.
  retailerName?: string | null;
  retailerCode?: string | null;
  paymentDate: string;
  amount: number;
  allocatedAmt: number;
  mode: string;
  referenceNo?: string | null;
  utr?: string | null;
  receivedBankAccountId?: string | null;
  // The resolved current account (from the list row) — used to show its name even if later deactivated.
  receivedBank?: { id: string; label?: string | null; bankName?: string | null; last4?: string | null } | null;
  attachmentUrl?: string | null;
  attachmentUrls?: string[] | null;
  // Read-only context, carried from the list row: what this payment IS, so the dialog can say so.
  approvalStatus?: string | null;
  orderNo?: string | null;
  orderId?: string | number | null;
  submittedByName?: string | null;
  submittedByRole?: string | null;
  rejectedReason?: string | null;
}

/**
 * Edit a recorded payment (correct a mistake). While any of it is already applied to invoices,
 * the retailer is locked and the amount can't drop below the applied total — the backend enforces
 * the same, and adjusts the retailer ledger with compensating entries.
 */
export function EditPaymentModal({ open, onClose, payment }: { open: boolean; onClose: () => void; payment: EditablePayment | null }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const allocated = Number(payment?.allocatedAmt ?? 0);
  const isApplied = allocated > 0.009;
  const minAmount = Math.max(1, allocated);
  // "Received in" belongs to money that went through a bank. Editing the mode to CASH takes the
  // question away, and the account with it — see PaymentFormModal for why a stale one is harmful.
  const mode = Form.useWatch('mode', form);
  const bankApplies = !!mode && mode !== 'CASH';

  // Searched on the server; the chosen retailer stays in the options (fetched by id if needed).
  const chosenRetailerId = Form.useWatch('retailerId', form);
  const retailerPick = useRetailerSearch({
    key: 'retailers-for-payment-edit',
    enabled: open && canViewRetailers,
    selected: chosenRetailerId,
  });
  /*
   * A picker's option list is not a lookup table.
   *
   * The options are ONE PAGE of retailers, and a select with no option matching its value
   * falls back to printing the value — so a payment belonging to retailer 201-and-beyond opened
   * with a bare "124" in the Retailer field, which is a database id shown to a human as if it
   * were a name. The same hole exists whenever the list has not loaded yet, and whenever the
   * retailer has since been deactivated out of the list.
   *
   * The row that opened this dialog already knows the name, so the current retailer is always
   * prepended when the fetched page does not contain them — the same guarantee the bank-account
   * picker below already makes for a deactivated account.
   */
  const retailerOpts = (() => {
    const opts: any[] = retailerPick.rows.map((r: any) => customerOption(r));
    const id = payment?.retailerId ? String(payment.retailerId) : '';
    if (id && !opts.some((o: any) => o.value === id)) {
      const name = payment?.retailerName;
      // Never a bare id: without a name we still say WHAT the number is. No place is on this fallback
      // because the payment row never carried one — inventing a blank capsule would read as "nowhere".
      const label = name ? [payment?.retailerCode, name].filter(Boolean).join(' · ') : `Retailer #${id}`;
      opts.unshift({ value: id, title: label, label });
    }
    return opts;
  })();

  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: open,
  });
  const bankOpts: any[] = Array.isArray(bankAccounts) ? bankAccounts : ((bankAccounts as any)?.data ?? []);
  // Show the payment's CURRENT account even if it was since deactivated (so it reads as a name, not a bare
  // id). The backend keeps an untouched value regardless of active state.
  const bankSelectOpts = (() => {
    const opts = bankOpts.map(bankAcctOption);
    const cur = payment?.receivedBank;
    if (cur?.id && !opts.some((o) => o.value === String(cur.id))) {
      opts.unshift({
        value: String(cur.id),
        searchLabel: bankAcctLabel(cur),
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{cur.bankName || cur.label || 'Bank'}{cur.last4 ? ` ··${cur.last4}` : ''}</span>
            <Tag style={{ margin: 0 }}>Inactive</Tag>
          </span>
        ),
      });
    }
    return opts;
  })();

  // Seed via initialValues + a per-payment key (below) so the form mounts fresh each open — avoids the
  // setFieldsValue-in-effect race against the Radix-modal remount (blank fields / a prior payment's
  // retailer lingering in the shared form store → wrong PATCH target).
  const initialValues = payment
    ? {
        retailerId: String(payment.retailerId),
        paymentDate: payment.paymentDate ? dayjs(payment.paymentDate) : dayjs(),
        amount: Number(payment.amount),
        mode: payment.mode,
        referenceNo: payment.referenceNo ?? undefined,
        utr: payment.utr ?? undefined,
        receivedBankAccountId: payment.receivedBankAccountId ?? undefined,
        attachmentUrls: (payment.attachmentUrls && payment.attachmentUrls.length)
          ? payment.attachmentUrls
          : (payment.attachmentUrl ? [payment.attachmentUrl] : []),
      }
    : undefined;

  const save = useConfirmedMutation({ title: "Save these payment changes?", content: "The retailer’s ledger is recalculated from the new figures.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => request({
      url: `/payments/${payment!.id}`,
      method: 'PATCH',
      data: {
        retailerId: vals.retailerId,
        paymentDate: vals.paymentDate ? vals.paymentDate.format('YYYY-MM-DD') : undefined,
        amount: vals.amount,
        mode: vals.mode,
        referenceNo: vals.referenceNo,
        utr: vals.utr,
        // Always sent: a value re-points the account, '' clears it. Switching the mode to CASH sends
        // '' whatever the (now hidden) picker still holds, so the row cannot keep an account it
        // cannot have.
        receivedBankAccountId: bankApplies ? (vals.receivedBankAccountId ?? '') : '',
        attachmentUrls: vals.attachmentUrls,
      },
    }),
    onSuccess: () => {
      message.success('Payment updated');
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['retailer-ledger'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update payment'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={payment ? `Edit payment ${payment.paymentNo}` : 'Edit payment'}
      width={680}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save changes</Button></>}
    >
      {/* What is being edited, before anything can be edited. The dialog used to open straight onto
          a grid of inputs, so the only way to tell WHICH payment you had was the title bar — and
          the facts a person needs in order to edit safely (is it approved? whose money is it? is
          any of it already applied?) were nowhere on screen. */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        <div>
          <div className="type-label text-muted">Amount</div>
          <div className="type-metric tabular-nums text-brand-700 dark:text-brand-400">{inr2(payment?.amount)}</div>
        </div>
        <div className="min-w-0">
          <div className="type-label text-muted">Retailer</div>
          <div className="truncate type-body-medium text-content">
            {payment?.retailerName
              ? <RetailerLink id={payment.retailerId}>{payment.retailerName}</RetailerLink>
              : <span className="text-subtle">#{payment?.retailerId}</span>}
            {payment?.retailerCode && <span className="ml-1.5 type-caption text-subtle">{payment.retailerCode}</span>}
          </div>
        </div>
        <div className="min-w-0">
          <div className="type-label text-muted">Against</div>
          <div className="type-body-medium text-content">{payment?.orderNo || <span className="text-subtle">On-account</span>}</div>
        </div>
        <div className="min-w-0">
          <div className="type-label text-muted">Submitted by</div>
          <div className="flex items-center gap-1.5 type-body-medium text-content">
            {payment?.submittedByName ?? <span className="text-subtle">—</span>}
            {payment?.submittedByRole && <Tag tone="blue" style={{ margin: 0 }}>{payment.submittedByRole}</Tag>}
          </div>
        </div>
        {payment?.approvalStatus && (
          <div>
            <div className="type-label text-muted">Status</div>
            <div className="mt-0.5">
              <Tag tone={(APPROVAL_TONE[payment.approvalStatus] ?? 'neutral') as any} style={{ margin: 0 }}>
                {APPROVAL_LABEL[payment.approvalStatus] ?? payment.approvalStatus}
              </Tag>
            </div>
          </div>
        )}
      </div>

      {isApplied && (
        <Alert type="info" style={{ marginBottom: 12 }}
          title={`${inr2(allocated)} of ${inr2(payment?.amount)} is already applied to invoices — ${inr2(Number(payment?.amount ?? 0) - allocated)} still unapplied.`}
          description="Retailer is locked and the amount can't go below the applied total. Reverse its allocations first to change more." />
      )}
      <Form key={payment?.id ?? 'edit'} form={form} initialValues={initialValues} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <div className="mb-2 type-label text-muted">Whose money, and when</div>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item name="retailerId" label="Retailer" rules={[{ required: true }]}>
              <Select {...CUSTOMER_SELECT_PROPS} filterOption={false} onSearch={retailerPick.onSearch} disabled={isApplied} options={retailerOpts} />
            </Form.Item>
          </Col>
          <Col span={6}>
            {/* No clear button on a field that is required: clearing it can only produce an error,
                and its × sat hard against the Amount box next door, reading as Amount's. */}
            <Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}>
              <DatePicker className="w-full" allowClear={false} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="amount" label="Amount (₹)"
              rules={[{ required: true }, { type: 'number', min: minAmount, message: `Min ${inr2(minAmount)} (already applied)` }]}>
              <InputNumber style={{ width: '100%' }} min={minAmount} step={100} />
            </Form.Item>
          </Col>
        </Row>

        <div className="mb-2 mt-2 type-label text-muted">How it was paid</div>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
              {/* Editing the mode to CASH drops the account with the question — the form keeps the
                  value of an unmounted item, so it is cleared here as well as on submit. */}
              <Select options={MODES.map((m) => ({ value: m }))}
                onChange={(v: any) => { if (v === 'CASH') form.setFieldsValue({ receivedBankAccountId: undefined }); }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="referenceNo" label="Reference No"><Input placeholder="Cheque / UPI ref" maxLength={40} /></Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="utr" label="UTR / Bank Ref"><Input maxLength={40} /></Form.Item>
          </Col>
          {bankApplies && (
            <Col span={24}>
              <Form.Item name="receivedBankAccountId" label="Received in (bank account)"
                rules={[{ required: true, message: 'Select the receiving bank account' }]}
                tooltip="Which company bank account this payment landed in.">
                <Select allowClear showSearch optionFilterProp="searchLabel"
                  placeholder={bankSelectOpts.length ? 'Select the receiving bank account' : 'No bank accounts configured'}
                  disabled={!bankSelectOpts.length}
                  options={bankSelectOpts} />
              </Form.Item>
            </Col>
          )}
        </Row>

        <div className="mb-2 mt-2 type-label text-muted">Proof</div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item name="attachmentUrls" label="Payment proof (receipts)"
              rules={[{ required: true, type: 'array', min: 1, message: 'At least one proof is required' }]}>
              <MultiFileUploadField category="receipt" buttonLabel="Add receipts" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
